import { ArrowLeft } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/catalyst/button';
import {
  useGetTemplateQuery,
  useListSectionsQuery,
} from '@/services/api/demandExampleTemplateService';

import { TemplateBuilder } from './TemplateBuilder';

export const TemplateEditPage = () => {
  const { demand_example_id, template_id } = useParams<{
    demand_example_id: string;
    template_id: string;
  }>();
  const navigate = useNavigate();

  const missingIds = !demand_example_id || !template_id;

  // Call hook unconditionally; skip when ids missing
  const {
    data: template,
    isFetching: loadingTpl,
    error: tplError,
  } = useGetTemplateQuery(
    { demandExampleId: demand_example_id ?? '', templateId: template_id ?? '' },
    { skip: missingIds },
  );

  const {
    data: sections,
    isFetching: loadingSecs,
    error: secError,
  } = useListSectionsQuery(
    { demandExampleId: demand_example_id ?? '', templateId: template_id ?? '' },
    { skip: missingIds },
  );

  if (missingIds) {
    return <p className="text-red-500 p-4 text-sm">Missing template id.</p>;
  }

  if (tplError || secError) {
    return (
      <p className="text-red-500 p-4 text-sm">
        Failed to load template data. Please refresh and try again.
      </p>
    );
  }

  if (loadingTpl || loadingSecs || !template || !sections) {
    return <p className="p-4 text-sm">Loading…</p>;
  }

  // Clean bounding boxes: keep only keys that are positive integers
  const cleanedSections = sections.map((sec) => {
    const bb = sec.bounding_boxes || {};
    const uniqByValue = new Map<
      string,
      [string, { x1: number; y1: number; x2: number; y2: number }]
    >();
    Object.entries(bb).forEach(([page, b]) => {
      const key = JSON.stringify(b); // value signature
      if (!uniqByValue.has(key))
        uniqByValue.set(key, [
          page,
          b as { x1: number; y1: number; x2: number; y2: number },
        ]);
    });
    const filtered = Object.fromEntries(Array.from(uniqByValue.values()));
    return { ...sec, bounding_boxes: filtered } as typeof sec;
  });

  // --------------------------------------------------------------
  // Debug logging – only in development to avoid noisy production
  // --------------------------------------------------------------
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.log('[TemplateEditPage] Raw sections', sections);
    // eslint-disable-next-line no-console
    console.log('[TemplateEditPage] Cleaned sections', cleanedSections);
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 p-4 border-b">
        <Button
          plain
          onClick={() => navigate(-1)}
          aria-label="Back"
          className="flex items-center gap-1"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <h2 className="text-lg font-semibold">Edit Template</h2>
      </div>
      <div className="flex-1 min-h-0">
        <TemplateBuilder
          demandExampleId={demand_example_id}
          template={template}
          existingSections={cleanedSections}
          // onClose navigates back to detail page
          onClose={() =>
            navigate(
              `/demand-examples/${demand_example_id}/templates/${template_id}`,
            )
          }
        />
      </div>
    </div>
  );
};
